import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ImageLightbox from '@/components/elements/ImageLightbox/ImageLightbox';
import VideoLightbox from '@/components/elements/ImageLightbox/VideoLightbox';
import ResponsiveImage from '@/components/elements/ResponsiveImage/ResponsiveImage';
import SanityImage from '@/components/elements/SanityImage/SanityImage';
import { IMAGE_SIZES } from '@/components/elements/ResponsiveImage/imageSizes';
import { motion } from 'framer-motion';
import { toLightboxItem, type MediaItem, type VideoItem } from './galleryMedia';

/** Same entrance as the hero carousel's text (see Carousel.tsx): fade in while dropping into
 * place from above. */
const cardItemVariants = {
  hidden: { opacity: 0, y: -80 },
  visible: { opacity: 1, y: 0 },
};

/** A batch of new tiles cascades in this many at a time before the stagger delay wraps back to
 * 0 — otherwise, on a page with more than ~15 tiles already loaded, every new tile's index is
 * past the cap and they'd all get the same (maximum) delay, killing the cascade. */
const STAGGER_BATCH = 12;

/**
 * Each tile's own entrance is offset a little further than the last within its batch, so a
 * batch of newly revealed images (the first load, or clicking "Load more") cascades in rather
 * than popping in all at once. Already-mounted tiles keep their key across renders, so the
 * entrance ("default") only ever plays for tiles that are genuinely new to the page.
 *
 * `layout` is separate: CSS columns rebalance ALL tiles across columns whenever the item count
 * changes (it isn't simple top-to-bottom append), so loading more images can shift where
 * already-visible tiles land — without `layout`, those already-mounted tiles would just jump
 * to their new spot instantly. It uses the SAME duration as the entrance (`default`) — a
 * repositioning tile and a freshly-entering tile should visibly move at the same speed, just
 * without the entrance's stagger delay (a tile that was already on screen shouldn't pause
 * before sliding to its new spot).
 */
const TILE_DURATION = 0.6;
const tileTransition = (index: number) => ({
  default: { duration: TILE_DURATION, delay: (index % STAGGER_BATCH) * 0.04 },
  layout: { duration: TILE_DURATION, ease: 'easeOut' },
});

const IMAGE_CLASS = 'w-full h-auto block transition-transform duration-300 hover:scale-110';

interface GalleryVideoProps {
  src: string;
  registerRef: (element: HTMLVideoElement | null) => void;
  /** Open the full-screen player. */
  onOpen: () => void;
  onPreviewStart: () => void;
  onPreviewEnd: () => void;
}

/**
 * Grid video tile. Hovering plays a muted preview; clicking opens the full-screen player
 * (see VideoLightbox). The file is only requested once the tile is (nearly) on screen, so a
 * gallery with many videos doesn't start downloading all of them up front.
 */
const GalleryVideo = ({ src, registerRef, onOpen, onPreviewStart, onPreviewEnd }: GalleryVideoProps) => {
  const containerRef = useRef<HTMLButtonElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || shouldLoad) return;

    if (!('IntersectionObserver' in window)) {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(container);

    return () => observer.disconnect();
  }, [shouldLoad]);

  return (
    <button
      ref={containerRef}
      type="button"
      aria-label="Play video full screen"
      onClick={onOpen}
      onMouseEnter={onPreviewStart}
      onMouseLeave={onPreviewEnd}
      className="relative block min-h-40 w-full cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-custom-hov"
    >
      <video
        ref={registerRef}
        src={shouldLoad ? src : undefined}
        className="w-full max-h-80 object-cover"
        muted
        playsInline
        preload="metadata"
        controls={false}
      />
      <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <svg
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          className="h-12 w-12 text-white"
          viewBox="0 0 64 64"
          fill="none"
          stroke="currentColor"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="32" cy="32" r="30" fill="rgba(0,0,0,0.5)" />
          <polygon points="26,20 46,32 26,44" fill="white" />
        </svg>
      </span>
    </button>
  );
};

interface GalleryGridProps {
  items: MediaItem[];
  /** Shown in place of the grid when `items` is empty. */
  emptyMessage?: string;
  /**
   * "light" (default): a white card with the caption (if any) in a strip below the photo —
   * used on the category pages. "dark": no visible card, the caption (if any) sits as a
   * gradient overlay on the photo itself — used on the gallery landing page's dark hero grid.
   */
  theme?: 'light' | 'dark';
}

/**
 * A photo's caption is only ever a title someone actually typed on the Sanity document — never
 * a designated stand-in (not the category name, not anything derived). A local photo has no
 * title field at all, so this is always null for those; a Sanity photo shows one only if its
 * document's Title field was actually filled in.
 */
function captionFor(item: MediaItem): string | null {
  return item.title;
}

/**
 * The gallery's media grid: photo tiles that open in `ImageLightbox`, video tiles that open in
 * `VideoLightbox`. Shared by the "all photos & videos" page and every category page, so the
 * (carefully bug-fixed) video playback behaviour only has one implementation.
 */
const GalleryGrid: React.FC<GalleryGridProps> = ({
  items,
  emptyMessage = "No photos or videos here yet.",
  theme = 'light',
}) => {
  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({});
  const [expandedVideoId, setExpandedVideoId] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const lightboxItems = useMemo(() => items.flatMap((item) => toLightboxItem(item) ?? []), [items]);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  // Only ONE copy of a video ever plays with sound: the full-screen player. Opening it
  // silences the tile's own preview, which would otherwise keep playing (and be heard)
  // behind the player.
  const openVideo = useCallback((id: string) => {
    const preview = videoRefs.current[id];
    if (preview) {
      preview.pause();
      preview.currentTime = 0;
    }
    setExpandedVideoId(id);
  }, []);
  const closeVideo = useCallback(() => setExpandedVideoId(null), []);

  // Hover preview: muted, and never while the full-screen player is open.
  const startPreview = (id: string) => {
    if (expandedVideoId !== null) return;
    // play() rejects if it is interrupted by pause() straight away; that's expected.
    videoRefs.current[id]?.play().catch(() => {});
  };

  const stopPreview = (id: string) => {
    const preview = videoRefs.current[id];
    if (!preview) return;
    preview.pause();
    preview.currentTime = 0;
  };

  const expandedVideo = items.find(
    (item): item is VideoItem => item.kind === 'video' && item.id === expandedVideoId
  );

  if (items.length === 0) {
    return (
      <p className={`text-center mt-10 ${theme === 'dark' ? 'text-white/60' : 'text-gray-500'}`}>{emptyMessage}</p>
    );
  }

  return (
    <>
      {/* CSS columns, not CSS grid: a grid row stretches every cell to its tallest sibling,
          which left blank space under any image shorter than the row's tallest. Columns let
          each tile keep its own height and pack tightly, like a masonry layout. */}
      <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-3">
        {items.map((item, index) => {
          const tileClassName =
            theme === 'dark'
              ? 'w-full mb-3 break-inside-avoid-column overflow-hidden rounded-xl relative'
              : 'w-full mb-3 break-inside-avoid-column overflow-hidden rounded-xl bg-white border border-gray-100 shadow-sm relative';

          const caption = captionFor(item);
          return (
          <motion.div
            key={item.id}
            layout="position"
            variants={cardItemVariants}
            initial="hidden"
            animate="visible"
            transition={tileTransition(index)}
            className={tileClassName}
          >
            {(item.kind === 'sanity-image' || item.kind === 'image') && (
              <button
                type="button"
                aria-label={`View larger: ${item.alt}`}
                onClick={() => setLightboxIndex(lightboxItems.findIndex((entry) => entry.id === item.id))}
                className="group relative block w-full cursor-zoom-in overflow-hidden focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-custom-hov"
              >
                {item.kind === 'sanity-image' ? (
                  <SanityImage
                    image={item.image}
                    alt={item.alt}
                    sizes={IMAGE_SIZES.gallery}
                    className={IMAGE_CLASS}
                  />
                ) : (
                  <ResponsiveImage
                    src={item.src}
                    srcSet={item.srcSet}
                    sizes={item.srcSet ? IMAGE_SIZES.gallery : undefined}
                    width={item.width}
                    height={item.height}
                    lqip={item.lqip}
                    alt={item.alt}
                    className={IMAGE_CLASS}
                  />
                )}
                {/* A hover cue every tile gets, caption or not — otherwise nothing signals a
                    plain, uncaptioned photo is clickable until the cursor already tells you. */}
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-300 group-hover:bg-black/20 group-hover:opacity-100">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-custom-darkblue shadow-md">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" />
                    </svg>
                  </span>
                </span>
              </button>
            )}
            {item.kind === 'video' && (
              <GalleryVideo
                src={item.src}
                registerRef={(element) => (videoRefs.current[item.id] = element)}
                onOpen={() => openVideo(item.id)}
                onPreviewStart={() => startPreview(item.id)}
                onPreviewEnd={() => stopPreview(item.id)}
              />
            )}
            {/* Only ever the item's own real title or its real category — never an invented caption. */}
            {theme === 'light' && item.title && (
              <p className="px-3 py-2.5 text-sm font-semibold text-custom-darkblue line-clamp-1">{item.title}</p>
            )}
            {theme === 'dark' && caption && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center gap-2 bg-gradient-to-t from-black/85 via-black/35 to-transparent px-3 pt-10 pb-2.5">
                <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-custom-hov">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <path d="M21 15l-5-5L5 21" />
                  </svg>
                </span>
                <span className="truncate text-xs font-semibold text-white">{caption}</span>
              </div>
            )}
          </motion.div>
          );
        })}
      </div>

      {lightboxIndex !== null && (
        <ImageLightbox
          items={lightboxItems}
          index={lightboxIndex}
          onClose={closeLightbox}
          onIndexChange={setLightboxIndex}
        />
      )}

      {expandedVideo && <VideoLightbox src={expandedVideo.src} onClose={closeVideo} />}
    </>
  );
};

export default GalleryGrid;
